import { FC } from "react";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import { FaEdit, FaTrash } from "react-icons/fa";
import { mapNodes } from "app/util/graph";
import { ContactListFieldsFragment, useContactListQuery } from "app/api/graph/types";
import { LoadingSpinner } from "app/component/util/LoadingSpinner";
import { deleteContactRoute, editContactRoute } from "app/route/Routes";
import { BlockButtonGroup } from "app/component/button/BlockButtonGroup";
import { ButtonLink } from "app/component/button/ButtonLink";
import { useGeneralPermissions } from "app/api/graph/hook/use-general-permissions";

type TableRowProps = {
    contact: ContactListFieldsFragment;
};

const TableRow = ({ contact }: TableRowProps) => {
    const permissions = useGeneralPermissions(contact.permissions);

    return (
        <tr key={contact.id}>
            <td>{contact.name}</td>
            <td>
                {permissions.canUpdate && (
                    <ButtonGroup>
                        <ButtonLink
                            to={editContactRoute.build({
                                contactId: contact._id,
                            })}
                            variant="warning"
                            size="sm"
                            title="Edit"
                        >
                            <FaEdit />
                        </ButtonLink>
                    </ButtonGroup>
                )}
                {permissions.canDelete && (
                    <ButtonGroup>
                        <ButtonLink
                            to={deleteContactRoute.build({
                                contactId: contact._id,
                            })}
                            variant="danger"
                            size="sm"
                            title="Delete"
                        >
                            <FaTrash />
                        </ButtonLink>
                    </ButtonGroup>
                )}
            </td>
        </tr>
    );
};
export const ContactsTable: FC = () => {
    const { data, loading, error, fetchMore } = useContactListQuery({
        notifyOnNetworkStatusChange: true,
    });
    const contacts = data?.contacts;

    if (loading && !contacts) {
        return <LoadingSpinner />;
    }
    if (error) {
        return <Alert variant="danger">{error.message}</Alert>;
    }
    if (!contacts) {
        return <Alert variant="danger">Not found.</Alert>;
    }

    const fetchMoreContact = () => fetchMore({ variables: { after: contacts.pageInfo.endCursor } });

    return (
        <Table responsive="sm" bordered className="crud-table">
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {mapNodes(contacts, (contact) => (
                    <TableRow key={contact.id} contact={contact} />
                ))}
                {contacts.edges?.length === 0 ? (
                    <tr>
                        <td colSpan={100}>No contacts.</td>
                    </tr>
                ) : null}
                {loading ? (
                    <tr>
                        <td colSpan={100}>
                            <LoadingSpinner />
                        </td>
                    </tr>
                ) : null}
                {contacts.pageInfo.hasNextPage ? (
                    <tr>
                        <td colSpan={100}>
                            <BlockButtonGroup>
                                <Button variant="light" onClick={fetchMoreContact}>
                                    Load more
                                </Button>
                            </BlockButtonGroup>
                        </td>
                    </tr>
                ) : null}
            </tbody>
        </Table>
    );
};
